import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';

interface ModalProps {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  /** While true, the backdrop/close button won't dismiss (an action is in flight). */
  isPending?: boolean;
  children: ReactNode;
  /** Footer actions (typically Cancel + a primary button). */
  footer: ReactNode;
}

/**
 * App-standard dialog shell: a bottom-sheet on mobile, a centered card on desktop,
 * dimmed backdrop, header with title/description + close button, scrollable body,
 * and a footer action row. Dismisses on backdrop click and the × unless `isPending`.
 *
 * Used by the people (staff) dialogs and the patient edit-details dialog — keep this
 * the single source for the shell so the two stay visually consistent.
 *
 * The panel is capped at the viewport height and only the BODY scrolls, so the
 * title and the action row stay reachable however long the content is. That cap
 * is what the roles dialog needs — 35 permission checkboxes across six
 * fieldsets is several screens tall.
 */
export function Modal({ title, description, onClose, isPending, children, footer }: ModalProps) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div className="flex max-h-[92dvh] w-full max-w-md flex-col rounded-t-2xl bg-paper sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-5">
          <div>
            <div className="text-[15px] font-semibold tracking-[-0.01em]">{title}</div>
            {description && <p className="mt-1 text-[12.5px] text-muted">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            disabled={isPending}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink disabled:opacity-50"
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        {/*
          `min-h-0` is load-bearing: a flex child defaults to `min-height:auto`,
          which refuses to shrink below its content, so the panel would grow
          past `max-h` and the footer would leave the screen again — the exact
          bug this replaced, just moved one element down.
        */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-3.5">
          {children}
        </div>
        <div className="flex shrink-0 gap-2 px-5 pb-5 pt-4">{footer}</div>
      </div>
    </div>
  );
}
